import { useState, useEffect } from 'react';
import { Input } from '@fyos/components';

const HEX_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

function isValidHex(hex: string): boolean {
  return HEX_REGEX.test(hex);
}

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  id?: string;
  size?: 'sm' | 'md';
}

export default function ColorPicker({ value, onChange, id, size = 'md' }: ColorPickerProps) {
  const [hexText, setHexText] = useState(value);

  useEffect(() => {
    setHexText(value);
  }, [value]);

  const handleColorInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    setHexText(color);
    onChange(color);
  };

  const handleHexInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let hex = e.target.value.trim();
    setHexText(hex);

    if (hex && !hex.startsWith('#')) {
      hex = '#' + hex;
    }

    if (isValidHex(hex)) {
      onChange(hex);
    }
  };

  const handleHexBlur = () => {
    let hex = hexText.trim();
    if (hex && !hex.startsWith('#')) {
      hex = '#' + hex;
    }
    if (isValidHex(hex)) {
      setHexText(hex);
      onChange(hex);
    } else {
      setHexText(value);
    }
  };

  const swatchSize = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';

  return (
    <div className="flex items-center gap-2">
      <div className={`${swatchSize} relative flex-shrink-0 rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden transition-shadow hover:ring-2 hover:ring-brand-500/30`}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundColor: value }}
        />
        <input
          id={id}
          type="color"
          value={value}
          onChange={handleColorInput}
          className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
        />
      </div>
      <Input
        value={hexText}
        onChange={handleHexInput}
        onBlur={handleHexBlur}
        className="flex-1 font-mono text-sm"
        placeholder="#000000"
        maxLength={7}
      />
    </div>
  );
}
